import GuidelinesStep from './GuidelinesStep';
import CategoryStep from './CategoryStep';
import ContentStep from './ContentStep';
import DurationStep from './DurationStep';
import { ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';
import type { CreateQuestionRequest } from '../types/Question.api.type';

export type QuestionCreateMode = 'create' | 'edit';

interface QuestionCreateProps {
  selectedCategory: string;
  handleCategoryChange: (selectedCategory: string) => void;
  step: number;
  handleNext: () => void;
  handlePrev: () => void;
  handleSubmit: () => void;
  createQuestionParam: CreateQuestionRequest;
  setCreateQuestionParamHandler: (key: keyof CreateQuestionRequest, value: string | string[] | number | boolean) => void;
  isContentStepValid: boolean;
  mode?: QuestionCreateMode;
}

const QuestionCreate = ({
  selectedCategory,
  handleCategoryChange,
  step,
  handleNext,
  handlePrev,
  handleSubmit,
  createQuestionParam,
  setCreateQuestionParamHandler,
  isContentStepValid,
  mode = 'create',
}: QuestionCreateProps) => {
  const isEdit = mode === 'edit';
  const headerTitle = isEdit ? '설문 수정' : '새 설문 작성';
  const headerDescription = isEdit ? '설문 내용을 수정해요' : '새로운 설문을 만들어요';
  const submitButtonText = isEdit ? '수정 완료' : '등록';

  const stepLabels = isEdit
    ? ['카테고리 설정', '설문 작성', '기간 설정']
    : ['작성 안내', '카테고리 설정', '설문 작성', '기간 설정'];

  const renderStepComponent = () => {
    if (isEdit) {
      // edit mode: 3단계 (카테고리 → 내용 → 기간) — 유의사항 스킵
      switch (step) {
        case 1:
          return <CategoryStep handleNext={handleNext} handlePrev={handlePrev} selectedCategory={selectedCategory}
                               handleCategoryChange={handleCategoryChange} />;
        case 2:
          return <ContentStep handleNext={handleNext} handlePrev={handlePrev} createQuestionParam={createQuestionParam}
                              setCreateQuestionParamHandler={setCreateQuestionParamHandler}
                              isContentStepValid={isContentStepValid} mode={mode} />;
        case 3:
          return <DurationStep handlePrev={handlePrev} handleSubmit={handleSubmit}
                               createQuestionParam={createQuestionParam}
                               setCreateQuestionParamHandler={setCreateQuestionParamHandler}
                               submitButtonText={submitButtonText} />;
        default:
          return <div>잘못된 접근입니다.</div>;
      }
    } else {
      // create mode: 4단계 (안내 → 카테고리 → 내용 → 기간)
      switch (step) {
        case 1:
          return <GuidelinesStep handleNext={handleNext} />;
        case 2:
          return <CategoryStep handleNext={handleNext} handlePrev={handlePrev} selectedCategory={selectedCategory}
                               handleCategoryChange={handleCategoryChange} />;
        case 3:
          return <ContentStep handleNext={handleNext} handlePrev={handlePrev} createQuestionParam={createQuestionParam}
                              setCreateQuestionParamHandler={setCreateQuestionParamHandler}
                              isContentStepValid={isContentStepValid} mode={mode} />;
        case 4:
          return <DurationStep handlePrev={handlePrev} handleSubmit={handleSubmit}
                               createQuestionParam={createQuestionParam}
                               setCreateQuestionParamHandler={setCreateQuestionParamHandler}
                               submitButtonText={submitButtonText} />;
        default:
          return <div>잘못된 접근입니다.</div>;
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="border-b p-4 flex items-center gap-3">
        {step > 1 && (
          <Button variant="ghost" size="sm" onClick={handlePrev} className="p-2">
            <ChevronLeft className="w-5 h-5" />
          </Button>
        )}
        <div>
          <p className="text-sm text-muted-foreground">{headerDescription}</p>
          <h1 className="text-xl font-bold">{headerTitle}</h1>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="px-4 pt-4">
        <div className="flex gap-2 mb-2">
          {stepLabels.map((label, index) => (
            <div key={label} className="flex-1">
              <div
                className={`h-2 rounded-full transition-all ${
                  index + 1 <= step ? 'bg-primary' : 'bg-gray-300'
                }`}
              />
            </div>
          ))}
        </div>
        <div className="flex text-xs text-muted-foreground">
          {stepLabels.map((label, index) => (
            <span key={label} className={`flex-1 ${index + 1 === step ? 'text-primary font-medium' : ''}`}>
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 flex flex-col p-6 overflow-y-auto">
        {renderStepComponent()}
      </div>
    </div>
  );
};

export default QuestionCreate;
